/**
 * The thin chrome the non-board representation surfaces (table / matrix / grid /
 * flow) carry. Those surfaces replace the board, so they never mount the board's
 * own `BoardChrome`; this bar re-hosts the same view-CHANNEL controls the board
 * shows top-right (health + builder + knobs + aspect colour), right-aligned to
 * match — the board-LAYOUT controls (recompute, reset placements, plane) are
 * meaningless here and stay board-only.
 *
 * There is NO view dropdown here (or on the board): views are chosen from the
 * navigation panel. Getting back to the board = pick the default there, close
 * the view tab, or Discard a live draft in the builder.
 */
import type { ReactElement } from "react";
import { ViewHealthBadge } from "./ViewHealthBadge.js";
import { ViewBuilder } from "./ViewBuilder.js";
import { KnobPicker } from "./KnobPicker.js";
import { AspectControls } from "../workbench/primitives/AspectControls.js";
import { EditToggle } from "../workbench/primitives/EditToggle.js";
import { ViewExportMenu } from "./export/ViewExportMenu.js";
import { useViewExportActions } from "./ViewExportContext.js";
import { useTranslation } from "../i18n";

export function ViewReprChrome({ canEdit }: { canEdit: boolean }): ReactElement {
  const { t } = useTranslation();
  const exportActions = useViewExportActions();
  return (
    <div
      data-hud
      className="flex items-center justify-end gap-2 border-b border-slate-800 bg-slate-900/(--tint-dim) px-3 py-1.5"
    >
      {/* The per-view edit toggle — same universal stance switch the board HUD
          carries; these surfaces gate cell/scaffold edits on it. */}
      <EditToggle canEdit={canEdit} />
      <ViewHealthBadge />
      {canEdit && <ViewBuilder />}
      <KnobPicker />
      <AspectControls variant="hud" />
      {/* Data export (CSV) for the headerless tabular surfaces, published by the
          active container via ViewExportContext. Empty ⇒ renders nothing. */}
      <ViewExportMenu actions={exportActions} label={t("indexExport")} />
    </div>
  );
}
